import { boundingExtent, buffer, containsCoordinate } from 'ol/extent';

import { PhotoLayerFeatureProperty as FeatureProperty } from './photo-layer-feature-property';
import { extentAt } from './photo-layer-utils';

export const EXPLODED_IMAGE_SIZE = 60;
export const DEFAULT_IMAGE_SIZE = 30;
const IMAGE_GAP = 8;
const CENTER_SIZE = 16;

const defaultLayouts = calculateDefaultLayouts(EXPLODED_IMAGE_SIZE, IMAGE_GAP).map((layout) => {
  layout.envelope = buffer(boundingExtent(layout), EXPLODED_IMAGE_SIZE / 2);
  return layout;
});

export class PhotoLayerFeatureLayout {

  constructor (feature) {
    const attachments = feature.get(FeatureProperty.ATTACHMENTS);
    this._defaultAttachmentPositions = defaultLayouts[Math.min(attachments.length, defaultLayouts.length) - 1];
  }

  get isDefault () {
    return !this._attachmentPositions;
  }

  get length () {
    return this.getAttachmentPositions().length;
  }

  getAttachmentPositions () {
    if (this._attachmentPositions) {
      return this._attachmentPositions;
    }
    return this._defaultAttachmentPositions;
  }

  getAttachmentPosition (attachmentIndex) {
    return this.getAttachmentPositions()[attachmentIndex];
  }

  setAttachmentPosition (attachmentIndex, x, y) {
    if (!this._attachmentPositions) {
      this._attachmentPositions = structuredClone(this._defaultAttachmentPositions);
    }
    this._attachmentPositions[attachmentIndex] = [ x, y ];
  }

  getAllEnvelopesAt (center) {
    return [ ...this._getAttachmentEnvelopesAt(center), extentAt(center, CENTER_SIZE) ];
  }

  getAttachmentAt (center, clickPoint) {
    return this._getAttachmentEnvelopesAt(center).findIndex(attachmentEnvelope => containsCoordinate(attachmentEnvelope, clickPoint));
  }

  getDisplacements () {
    if (!this._attachmentPositions) {
      return [];
    }

    const displacements = [];
    for (let i = 0; i < this._attachmentPositions.length; i++) {
      if (!pointsEqual(this._attachmentPositions[i], this._defaultAttachmentPositions[i])) {
        displacements.push({
          from: this._defaultAttachmentPositions[i],
          to: this._attachmentPositions[i]
        });
      }
    }
    return displacements;
  }

  _getAttachmentEnvelopesAt (center) {
    return this._getLayoutPointsAt(center).map(layoutPoint => extentAt(layoutPoint, EXPLODED_IMAGE_SIZE));
  }

  _getLayoutPointsAt ([ centerX, centerY ]) {
    return this.getAttachmentPositions().map(([ x, y ]) => [ centerX + x, centerY + y ]);
  }

}

function pointsEqual (point1, point2) {
  return point1[0] === point2[0] && point1[1] === point2[1];
}

function calculateDefaultLayouts (size, gap) {
  return [
    [
      [ 0, -(size / 2 + 2 * gap) ]
    ],
    [
      [ 0, -(size / 2 + 2 * gap) ],
      [ 0, +(size / 2 + 2 * gap) ]
    ],
    [
      [ 0, -(size / 2 + 2 * gap) ],
      [ -(size / 2 + 2 * gap), +(size / 2 + 2 * gap) ],
      [ +(size / 2 + 2 * gap), +(size / 2 + 2 * gap) ]
    ],
    [
      [ +(size / 2 + 2 * gap), -(size / 2 + 2 * gap) ],
      [ +(size / 2 + 2 * gap), +(size / 2 + 2 * gap) ],
      [ -(size / 2 + 2 * gap), +(size / 2 + 2 * gap) ],
      [ -(size / 2 + 2 * gap), -(size / 2 + 2 * gap) ]
    ],
    [
      [ 0, -(size / 2 + 2 * gap) ],
      [ +(size + gap), -(size / 2 - gap) ],
      [ +(size / 2 + gap / 2), +(size / 2 + 2 * gap) ],
      [ -(size / 2 + gap / 2), +(size / 2 + 2 * gap) ],
      [ -(size + gap), -(size / 2 - gap) ]
    ],
    [
      [ 0, -(size / 2 + 2 * gap) ],
      [ +(size + gap), -(size / 2 + gap / 2) ],
      [ +(size + gap), +(size / 2 + gap / 2) ],
      [ 0, +(size / 2 + 2 * gap) ],
      [ -(size + gap), +(size / 2 + gap / 2) ],
      [ -(size + gap), -(size / 2 + gap / 2) ]
    ],
    [
      [ 0, -(size + 3 * gap) ],
      [ +(size + gap), -(size + gap) ],
      [ +(size + 3 * gap), 0 ],
      [ +(size / 2 + gap / 2), +(size + gap) ],
      [ -(size / 2 + gap / 2), +(size + gap) ],
      [ -(size + 3 * gap), 0 ],
      [ -(size + gap), -(size + gap) ]
    ],
    [
      [ 0, -(size + 3 * gap) ],
      [ +(size + gap), -(size + gap) ],
      [ +(size + 3 * gap), 0 ],
      [ +(size + gap), +(size + gap) ],
      [ 0, +(size + 3 * gap) ],
      [ -(size + gap), +(size + gap) ],
      [ -(size + 3 * gap), 0 ],
      [ -(size + gap), -(size + gap) ]
    ]
  ];
}
